import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { Factory, Users, Globe, Award } from 'lucide-react';

// Değerler
const VALUES = [
  {
    icon: Factory,
    title: 'Modern Üretim',
    description: '10.000 m² kapalı alanda, son teknoloji CNC makineleri ve entegre üretim hatları.',
  },
  {
    icon: Users,
    title: 'Uzman Kadro',
    description: '150+ deneyimli usta ve mühendisten oluşan profesyonel ekip.',
  },
  {
    icon: Globe,
    title: 'Global Erişim',
    description: '25+ ülkeye ihracat, uluslararası kalite standartlarında üretim.',
  },
  {
    icon: Award,
    title: 'Kalite Taahhüdü',
    description: 'ISO 9001 sertifikalı süreçler, titiz kalite kontrol.',
  },
];

// Animation variants - ProjectsPage ile aynı
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut' as const,
    },
  },
};

export function AboutPage() {
  return (
    <>
      <title>Kurumsal | Turco Sandalye</title>

      {/* Hero Section - ProjectsPage ile aynı yapı */}
      <section className="bg-charcoal pt-36 pb-16 md:pt-40 md:pb-20 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/80 to-charcoal" />
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-gold text-xs md:text-sm font-semibold uppercase tracking-widest mb-4 block"
          >
            1985'ten Bu Yana
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl mb-6"
          >
            Turco Sandalye
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-400 font-light leading-relaxed"
          >
            Türk mobilya sektörünün öncü markası olarak, mekanların karakterini şekillendiriyoruz.
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Video - 360 Dönen Sandalye */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative flex items-center justify-center"
            >
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full max-w-[550px] h-auto"
              >
                <source src="/videos/chair-rotate.mp4" type="video/mp4" />
              </video>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span className="text-gold text-xs font-semibold uppercase tracking-widest mb-4 block">
                Hikayemiz
              </span>
              <h2 className="font-display text-3xl md:text-4xl text-charcoal mb-6">
                40 Yıllık Ustalık
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  1985 yılında küçük bir atölyede başlayan yolculuğumuz, bugün 10.000 m²'lik modern üretim tesisimizde devam ediyor.
                </p>
                <p>
                  Modern teknolojiyi geleneksel ustalıkla harmanlayarak, Türkiye'nin önde gelen otelleri, restoranları ve kurumsal mekanları için mobilya üretiyoruz.
                </p>
                <p>
                  Her projede müşterilerimizin güvenini kazanmak, kaliteye olan bağlılığımızın en büyük kanıtı.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="bg-stone-50 py-16 md:py-24 border-y border-gray-100">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="text-gold text-xs font-semibold uppercase tracking-widest mb-4 block">
              Neden Biz
            </span>
            <h2 className="font-display text-3xl md:text-4xl text-charcoal">
              Farkımız
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
          >
            {VALUES.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white p-6 md:p-8 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-charcoal" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg text-charcoal mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section - ProjectsPage ile aynı */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="font-display text-4xl md:text-5xl text-charcoal block mb-2">1985</span>
              <span className="text-gray-500 text-sm md:text-base">Kuruluş Yılı</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <span className="font-display text-4xl md:text-5xl text-charcoal block mb-2">10.000</span>
              <span className="text-gray-500 text-sm md:text-base">m² Üretim Alanı</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="font-display text-4xl md:text-5xl text-charcoal block mb-2">150+</span>
              <span className="text-gray-500 text-sm md:text-base">Uzman Personel</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <span className="font-display text-4xl md:text-5xl text-charcoal block mb-2">25+</span>
              <span className="text-gray-500 text-sm md:text-base">İhracat Ülkesi</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - ProjectsPage ile aynı */}
      <section className="bg-charcoal text-white py-20 md:py-24 text-center border-b border-white/10">
        <div className="container mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-3xl md:text-4xl mb-6"
          >
            Projenizi Birlikte Hayata Geçirelim
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-400 mb-10 max-w-xl mx-auto"
          >
            Otel, restoran veya kurumsal projeniz için profesyonel çözüm ortağınız olmaya hazırız.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to="/contact"
              className="inline-block bg-white text-charcoal px-10 py-4 text-sm font-bold uppercase tracking-widest hover:bg-gold transition-colors"
            >
              İletişime Geçin
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
